import React, { Fragment } from 'react'
import { TextLink } from 'secondstep-components'
import PropTypes from 'prop-types'
import { domains } from 'config/env'
import { formatLastUpdatedRelativeTime } from 'utils/reportsHelpers'
import { TOOLTIP_LOCATIONS, TOOLTIP_PROGRAMS } from 'utils/tooltipHelpers'
import AdminExportDownloadButton from 'components/AdminExportDownloadButton'
import {
  Bold,
  Bullet,
  Classes,
  LastUpdatedMessage,
  LeftBox,
  LessonCompletionOverviewTitleRow,
  RightBox,
  SchoolTitle,
  SchoolsAndClasses,
  SiteHeaderWrapper,
  StyledHeaderOne,
  Summary,
  TierPill,
  Totals,
  UnitTitle,
} from '../component.styles'
import {
  DIGITAL_WELL_BEING_UNIT_PILL_TEXT,
  DIGITAL_WELL_BEING_UNIT_LABEL,
  LESSON_COMPLETION_SPECIALIZED_UNITS_LABEL,
  LESSON_COMPLETION_SPECIALIZED_SUMMARY_TEXT,
  SITE_HEADER_OVERVIEW_BASE,
} from '../constants'

const AdminSiteHeader = ({
  classesCreated,
  classesNotStarted,
  etlLastUpdatedAt: lastUpdatedAt,
  hideSummary,
  isSpecializedUnits,
  lessonsCompleted,
  lessonCompletionUnitsLabel = 'Core Units',
  lessonsTotal,
  showLessonCompletionExport = true,
  showSummaryText,
  singleSite,
  siteId,
  siteName,
  sitesTotal,
}) => {
  const classesNotStartedStr = `class${classesNotStarted === 1 ? '' : 'es'} ha${
    classesNotStarted === 1 ? `sn't` : `ven't`
  } completed a lesson yet`
  const classesStr = `Class${classesCreated === 1 ? '' : 'es'}${
    isSpecializedUnits ? '' : ' Created'
  }`
  const lastUpdatedMessage = formatLastUpdatedRelativeTime(lastUpdatedAt)
  const lessonsCompletedPercentage =
    Math.floor((lessonsCompleted / lessonsTotal) * 100) || 0
  const lessonsCompletedStr = `${lessonsCompletedPercentage}% of Lessons Completed`
  const schoolsStr = !singleSite && `School${sitesTotal === 1 ? '' : 's'}`

  const primaryHeaderTitle = `${SITE_HEADER_OVERVIEW_BASE}, ${lessonCompletionUnitsLabel}`
  const showSpecializedDwbPill =
    Boolean(lessonCompletionUnitsLabel) &&
    lessonCompletionUnitsLabel === LESSON_COMPLETION_SPECIALIZED_UNITS_LABEL &&
    !singleSite

  const trainingReportSlug =
    lessonCompletionUnitsLabel === LESSON_COMPLETION_SPECIALIZED_UNITS_LABEL
      ? 'digital-well-being'
      : 'core'
  const trainingReportHref = `${domains.ADMIN_DOMAIN}/training-report/${trainingReportSlug}/${siteId}`

  return (
    <SiteHeaderWrapper>
      <LeftBox>
        {singleSite && <SchoolTitle>{siteName?.toUpperCase()}</SchoolTitle>}
        {showSpecializedDwbPill && !isSpecializedUnits ? (
          <LessonCompletionOverviewTitleRow>
            <StyledHeaderOne $inTitleRow>{primaryHeaderTitle}</StyledHeaderOne>
            <TierPill data-testid="digital-well-being-unit-pill">
              <span>{DIGITAL_WELL_BEING_UNIT_PILL_TEXT}</span>
            </TierPill>
          </LessonCompletionOverviewTitleRow>
        ) : (
          !isSpecializedUnits && (
            <StyledHeaderOne>{primaryHeaderTitle}</StyledHeaderOne>
          )
        )}
        {showSummaryText && (
          <Summary>
            <SchoolsAndClasses>
              <Classes>{LESSON_COMPLETION_SPECIALIZED_SUMMARY_TEXT}</Classes>
            </SchoolsAndClasses>
          </Summary>
        )}
        {!hideSummary && (
          <Fragment key="summary-key">
            <Summary>
              <SchoolsAndClasses>
                {!singleSite && (
                  <Classes key="multi-site-1">
                    <Bold>{sitesTotal}</Bold> {schoolsStr}
                    <Bold>{` • `}</Bold>
                    <Bold>{classesCreated}</Bold> {classesStr}
                    {classesNotStarted !== 0 && (
                      <Fragment key="classes-not-started-key">
                        <span className="pipe">|</span>
                        <span>
                          <Bullet /> {classesNotStarted} {classesNotStartedStr}
                        </span>
                      </Fragment>
                    )}
                  </Classes>
                )}
                {singleSite && (
                  <Fragment key="single-site-1">
                    {isSpecializedUnits && (
                      <UnitTitle>{DIGITAL_WELL_BEING_UNIT_LABEL}</UnitTitle>
                    )}
                    <Totals>
                      <Classes>
                        <Bold>{classesCreated}</Bold> {classesStr}
                      </Classes>
                      {classesNotStarted !== 0 && (
                        <Fragment key="classes-not-started-key">
                          <span className="pipe">|</span>
                          <Classes>
                            <Bullet /> {classesNotStarted}{' '}
                            {classesNotStartedStr}
                          </Classes>
                        </Fragment>
                      )}
                    </Totals>
                  </Fragment>
                )}
              </SchoolsAndClasses>
            </Summary>
            {isSpecializedUnits && (
              <Totals isSpecializedUnits={isSpecializedUnits}>
                <Classes>{lessonsCompletedStr}</Classes>
                <span className="pipe">|</span>
                <TextLink
                  aria-label="View training progress report"
                  dataTestId="training-progress-report-link"
                  href={trainingReportHref}
                  target="_self"
                >
                  Training Progress Report
                </TextLink>
              </Totals>
            )}
          </Fragment>
        )}
      </LeftBox>
      <RightBox>
        {!isSpecializedUnits && (
          <LastUpdatedMessage>{lastUpdatedMessage}</LastUpdatedMessage>
        )}
        {showLessonCompletionExport &&
          classesCreated > 0 &&
          !isSpecializedUnits && (
            <AdminExportDownloadButton
              programKey={TOOLTIP_PROGRAMS.K8_MONITOR}
              tooltipLocation={TOOLTIP_LOCATIONS.EXPORT_BUTTON}
            />
          )}
      </RightBox>
    </SiteHeaderWrapper>
  )
}

AdminSiteHeader.propTypes = {
  classesCreated: PropTypes.number,
  classesNotStarted: PropTypes.number,
  etlLastUpdatedAt: PropTypes.string,
  hideSummary: PropTypes.bool,
  isSpecializedUnits: PropTypes.bool,
  lessonCompletionUnitsLabel: PropTypes.string,
  lessonsCompleted: PropTypes.number,
  lessonsTotal: PropTypes.number,
  showLessonCompletionExport: PropTypes.bool,
  showSummaryText: PropTypes.bool,
  singleSite: PropTypes.bool,
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  siteName: PropTypes.string,
  sitesTotal: PropTypes.number,
}

export default AdminSiteHeader
